/**
 * Appsmith-style **data source** field (ADR-0044 U-track polish) — the inspector
 * control for a query-bound widget's `data` prop.
 *
 *   - **JS off** (default) — a clean **query DROPDOWN**: pick a declared page
 *     query and the field stores `{{ $queries.<name>.data }}` (the reactive
 *     binding, kept by the renderer). This is the Appsmith "Table Data → pick a
 *     query" no-code path that was missing (the field used to be a raw text box).
 *   - **JS on** — the inline `{{ }}` editor for an ARBITRARY array expression
 *     (`{{ Deals.filter(...) }}`), a static client list.
 *
 * Mode is DERIVED from the value: a plain `{{ $queries.<name>.data }}` (or empty)
 * → dropdown; any other expression → JS editor (forced). The `JS` badge toggles
 * between them for the plain/empty case. Stays in lockstep with the runtime
 * (`effectiveQueryRef` reads the same binding).
 */
import * as React from "react";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import { bindingForQueryRef, queryRefFromBinding } from "@/lib/dataBinding";
import type { ExprPreview } from "@/lib/pagePreview";
import type { ResourceQuery } from "@/lib/resourceKinds";

import { JsBadge } from "./BindableField";
import { ResourcePicker } from "./ResourcePicker";

const fieldLabel = "text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70";

export interface DataSourceFieldProps {
  label: string;
  value: unknown;
  /** Declared page-query names — the picker options. */
  queryNames: string[];
  /** Declared page queries WITH their `source_kind` — drives the segmented picker
   *  (Tabelle / Dataset / HTTP / JS). Falls back to `queryNames` (kind `other`). */
  queries?: ResourceQuery[];
  jsAmbientTypes?: string;
  previewExpr?: (template: string) => ExprPreview | null;
  registerInsert?: (insert: ((text: string) => void) | null) => void;
  onChange: (value: unknown) => void;
}

export function DataSourceField({
  label,
  value,
  queryNames,
  queries,
  jsAmbientTypes,
  previewExpr,
  registerInsert,
  onChange,
}: DataSourceFieldProps) {
  // A plain `{{ $queries.<name>.data }}` binding → its query name (else null).
  const refName = queryRefFromBinding(value);
  const isString = typeof value === "string";
  // An arbitrary expression (a `{{ }}` that is NOT a plain query ref) forces JS.
  const isArbitrary = isString && (value as string).includes("{{") && refName === null;

  const [jsMode, setJsMode] = React.useState(isArbitrary);
  React.useEffect(() => {
    if (isArbitrary) setJsMode(true);
  }, [isArbitrary]);

  const toggle = () => {
    if (jsMode) {
      // Leaving JS: keep a plain query ref (so the dropdown shows it), else clear.
      if (refName === null) onChange("");
      setJsMode(false);
    } else {
      // Entering JS: seed the current binding so there's something to edit.
      onChange(refName ? bindingForQueryRef(refName) : "{{  }}");
      setJsMode(true);
    }
  };

  // Insert-at-cursor wiring (Data panel → caret) while the JS editor is focused.
  type MonacoEditor = Parameters<
    NonNullable<React.ComponentProps<typeof CodeEditor>["onEditorMount"]>
  >[0];
  React.useEffect(() => () => registerInsert?.(null), [registerInsert]);
  const onEditorMount = React.useCallback(
    (editor: MonacoEditor) => {
      if (!registerInsert) return;
      const insert = (text: string) => {
        const sel = editor.getSelection();
        if (sel) editor.executeEdits("insert-binding", [{ range: sel, text, forceMoveMarkers: true }]);
        editor.focus();
      };
      editor.onDidFocusEditorText(() => registerInsert(insert));
    },
    [registerInsert],
  );

  const expr = jsMode || isArbitrary;
  // Prefer the typed `queries` (segments the picker by source_kind); fall back to
  // bare names (kind `other`) when only `queryNames` was threaded.
  const pickerQueries: ResourceQuery[] = queries ?? queryNames.map((name) => ({ name }));

  return (
    <div className="flex flex-col gap-1.5 border-b border-border/50 py-2 last:border-0">
      <div className="flex items-center justify-between gap-3">
        <span className={`flex items-center gap-1.5 ${fieldLabel}`}>
          {label}
          <JsBadge active={expr} onToggle={toggle} />
        </span>
      </div>

      {expr ? (
        <>
          <div className="h-24 w-full">
            <CodeEditor
              value={isString ? (value as string) : ""}
              onChange={onChange}
              language="javascript"
              height="100%"
              inline
              jsAmbientTypes={jsAmbientTypes}
              onEditorMount={onEditorMount}
            />
          </div>
          <ExprPreviewLine previewExpr={previewExpr} value={isString ? (value as string) : ""} />
          <p className="text-[11px] leading-snug text-muted-foreground/80">
            Espressione array, es. <code className="font-mono">{"{{ $queries.recent.data }}"}</code> o{" "}
            <code className="font-mono">{"{{ Deals.filter(d => d.won) }}"}</code>.
          </p>
        </>
      ) : (
        <>
          <ResourcePicker
            queries={pickerQueries}
            value={refName ?? ""}
            align="left"
            onSelect={(name) => onChange(name ? bindingForQueryRef(name) : "")}
          />
          {queryNames.length === 0 && (
            <p className="text-[11px] leading-snug text-muted-foreground/80">
              Nessuna query. Creane una nella scheda <strong>Query</strong> del
              pannello Dati, poi selezionala qui.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Live `= value` (success tone) or the evaluation error under the JS editor. */
function ExprPreviewLine({
  previewExpr,
  value,
}: {
  previewExpr?: (template: string) => ExprPreview | null;
  value: string;
}) {
  if (!previewExpr) return null;
  const preview = previewExpr(value);
  if (!preview) return null;
  return preview.ok ? (
    <p
      data-testid="expr-preview"
      className="truncate font-mono text-[11px] text-muted-foreground"
      title={preview.display}
    >
      <span className="text-success">= </span>
      <span>{preview.display}</span>
    </p>
  ) : (
    <p
      data-testid="expr-preview-error"
      className="truncate text-[11px] text-danger/80"
      title={preview.error}
    >
      {preview.error}
    </p>
  );
}
